import {
  getProductQuery,
  MONGO_REALM_COLLECTIONS
} from '@setvi/shared/services';
import { useQuery } from '@tanstack/react-query';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { Customer } from 'Services/MongoRealm/Types';
import { Product } from '@setvi/shared/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  CompanyProductUserType,
  CompanySettingsTypeID
} from '@setvi/shared/services/react-query/query/user/types';
import { useSnackbar } from 'notistack';
import { getSingleSalesKitByName } from 'Services';
import {
  AnalyticsModules,
  SalesEnablementActionType,
  useAnalytics
} from '@setvi/shared/hooks';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';

import { DetailsDialogProps } from '..';
import { useSalesKitStore } from '../store';

export const useProductDetails = ({
  selectedProduct,
  selectedCustomerId,
  client,
  userType
}: Pick<
  DetailsDialogProps,
  'selectedCustomerId' | 'client' | 'userType' | 'selectedProduct'
>) => {
  const flags = useFlags();

  const [product, setProduct] = useState<Product>();
  const [customer, setCustomer] = useState<Customer>();
  const [isLoading, setIsLoading] = useState(true);
  const { companyData } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();

  const { captureAnalytics } = useAnalytics();

  const { data, isFetching } = useQuery({
    ...getProductQuery({ productId: selectedProduct?._id }),
    enabled: !!selectedProduct?._id
  });

  const fetchProduct = useCallback(
    async (p: Product) => {
      setIsLoading(true);
      try {
        if (!selectedProduct) return;

        const getProductPrice = () => {
          if (userType === CompanyProductUserType.Manufacturer) return p.price;

          if (Number.isFinite(selectedProduct.price))
            return selectedProduct.price;

          return null;
        };

        setProduct({
          ...p,
          name: toTitleCase(p.name),
          ...(p.vendor && {
            vendor: {
              ...p.vendor,
              name: toTitleCase(p.vendor.name)
            }
          }),
          price: getProductPrice(),
          ...(Number.isFinite(selectedProduct.cost)
            ? { cost: selectedProduct.cost }
            : {})
        });
      } catch (e) {
        enqueueSnackbar('Error occurred while fetching product data.', {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar, selectedProduct, userType]
  );

  const handleRealmFetch = async (sku: string) => {
    const response = await client
      .db(
        companyData.Settings[CompanySettingsTypeID.AtlasMongoProductDBName]
          ?.Value
      )
      .collection(MONGO_REALM_COLLECTIONS.PRODUCTS)
      .findOne({ sku });

    fetchProduct(response);
  };

  const fetchCustomer = useCallback(
    async (customerId: Customer['_id']) => {
      setIsLoading(true);
      try {
        const response: Customer = await client
          .db(
            companyData.Settings[CompanySettingsTypeID.AtlasMongoProductDBName]
              ?.Value
          )
          .collection(MONGO_REALM_COLLECTIONS.ACCOUNTS)
          .findOne({ _id: customerId });

        setCustomer(response);
      } catch (e) {
        enqueueSnackbar('Error occured while fetching customer data', {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    },
    [client, companyData.Settings, enqueueSnackbar]
  );

  const updateDialogProduct = (newValues: Partial<Product>) =>
    setProduct({ ...product, ...newValues });

  useEffect(() => {
    if (!isFetching) {
      if (data) {
        fetchProduct(data);
      } else {
        handleRealmFetch(selectedProduct?.sku);
      }
      if (selectedCustomerId) fetchCustomer(selectedCustomerId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.sku, data, isFetching]);

  // Fetch sales enablement content

  const {
    data: salesKit,
    isFetching: isSalesKitFetching,
    error: salesKitError
  } = useQuery({
    ...getSingleSalesKitByName(
      selectedProduct?._id || product?._id,
      'AI Overview'
    ),
    enabled:
      (!!selectedProduct?._id || !!product?._id) && flags?.salesEnablement,
    retry: false
  });

  const { setSalesKit, setSalesKitError, setSalesKitLoading } =
    useSalesKitStore();

  useEffect(() => {
    setSalesKitLoading(isSalesKitFetching);

    if (salesKit) {
      setSalesKit(salesKit);
    }

    if (salesKitError) {
      const msg = (salesKitError as Error).message;
      setSalesKitError(msg);
      captureAnalytics(
        {
          error: msg || '',
          productSKU: product?.sku || selectedProduct?.sku,
          actionType: SalesEnablementActionType.Error
        },
        AnalyticsModules.SalesEnablement
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    salesKit,
    isSalesKitFetching,
    salesKitError,
    setSalesKit,
    setSalesKitLoading,
    setSalesKitError
  ]);

  return {
    product,
    customer,
    salesKit,
    isLoading: isLoading || isFetching,
    salesKitError,
    isSalesKitFetching,
    updateDialogProduct
  };
};
